import React, { createContext, useContext, useState, useRef, useCallback, ReactNode } from 'react'
import { ImageLightbox } from '../components/Editor/ImageLightbox'

interface ClipboardHandlers {
  copy: () => void | Promise<void>
  paste: () => void | Promise<void>
}

interface EditorContextType {
  activeEditor: any | null
  setActiveEditor: (editor: any | null) => void
  /** ★ editor 参数：多编辑器保活（多 tab / 双模式槽 / 速记懒加载）下同一时刻有
   * 多个 TipTapEditor 实例注册回调；必须携带实例身份，触发时路由到"当前聚焦
   * 编辑器"的回调。否则最后挂载的实例胜出 → 图片插错笔记 + focus 引发标签切换 */
  setOnImageUpload: (fn: (() => void) | null, editor?: any) => void
  triggerImageUpload: () => void
  /** ★ 复制/粘贴回调（同多实例路由：按 activeEditor 触发）：移动端工具栏 / 选择气泡
   * 共用编辑器实例的官方复制粘贴实现（handleCopy/handlePaste），与右键菜单行为一致 */
  setOnClipboardAction: (handlers: ClipboardHandlers | null, editor?: any) => void
  triggerClipboardCopy: () => void
  triggerClipboardPaste: () => void
  /** ★ 移到最新速记页（同多实例路由：按 activeEditor 触发）：
   * 移动端键盘工具栏按钮与右键菜单共用 TipTapEditor 实例的 moveToLatestSuji 实现 */
  setOnMoveToLatestSuji: (fn: (() => void) | null, editor?: any) => void
  triggerMoveToLatestSuji: () => void
  /** 当前聚焦编辑器是否支持"移到最新速记页"（仅速记模式实例注册）：工具栏据此条件渲染按钮 */
  activeEditorCanSuji: boolean
  /** ★ 全局图片预览（NodeView 重建不影响；InlineImage 展开/双击调用）
   * gallery：同组图片列表+当前索引（多图预览支持左右滑动切换） */
  openImagePreview: (src: string, alt?: string, gallery?: { images: string[]; index: number }) => void
  closeImagePreview: () => void
}

const EditorContext = createContext<EditorContextType | undefined>(undefined)

export const useEditorContext = () => {
  const context = useContext(EditorContext)
  if (context === undefined) {
    throw new Error('useEditorContext must be used within an EditorProvider')
  }
  return context
}

export const EditorProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [activeEditor, setActiveEditor] = useState<any | null>(null)
  // ★ 编辑器实例 → 上传回调 映射（多实例注册；触发时按 activeEditor 路由）
  const uploadHandlersRef = useRef<Map<any, () => void>>(new Map())
  // 旧式无 editor 参数注册的回退槽（当前无调用方，保守兼容）
  const lastUploadHandlerRef = useRef<(() => void) | null>(null)
  // ★ 全局图片预览状态（模块级持久，NodeView 重建不影响）
  const [preview, setPreview] = useState<{
    src: string;
    alt?: string;
    images?: string[];
    index?: number;
  } | null>(null)

  // ★ 复制/粘贴回调注册表（同图片上传：多实例 Map + 老式槽回退）
  const clipboardHandlersRef = useRef<Map<any, ClipboardHandlers>>(new Map())
  const lastClipboardHandlersRef = useRef<ClipboardHandlers | null>(null)

  const setOnClipboardAction = useCallback((handlers: ClipboardHandlers | null, editor?: any) => {
    if (editor) {
      if (handlers) {
        clipboardHandlersRef.current.set(editor, handlers)
      } else {
        clipboardHandlersRef.current.delete(editor)
      }
    } else {
      lastClipboardHandlersRef.current = handlers
    }
  }, [])

  const dispatchClipboardAction = useCallback((which: 'copy' | 'paste') => {
    const active = activeEditor && !activeEditor.isDestroyed ? activeEditor : null
    const handlers = (active && clipboardHandlersRef.current.get(active)) || lastClipboardHandlersRef.current
    if (!handlers) return
    const fn = which === 'copy' ? handlers.copy : handlers.paste
    try {
      fn?.()
    } catch (e) {
      console.warn(`[EditorContext] clipboard ${which} 触发失败:`, e)
    }
  }, [activeEditor])

  const triggerClipboardCopy = useCallback(() => dispatchClipboardAction('copy'), [dispatchClipboardAction])
  const triggerClipboardPaste = useCallback(() => dispatchClipboardAction('paste'), [dispatchClipboardAction])

  // ★ "移到最新速记页"回调注册表：state Map（非 ref）——注册/注销变化需触发
  // 重渲染，让工具栏的 activeEditorCanSuji 布尔值保持实时（按钮条件渲染）
  const [sujiHandlers, setSujiHandlers] = useState<Map<any, () => void>>(new Map())

  const setOnMoveToLatestSuji = useCallback((fn: (() => void) | null, editor?: any) => {
    setSujiHandlers(prev => {
      const next = new Map(prev)
      if (editor) {
        if (fn) {
          next.set(editor, fn)
        } else {
          next.delete(editor)
        }
      }
      return next
    })
  }, [])

  const triggerMoveToLatestSuji = useCallback(() => {
    const active = activeEditor && !activeEditor.isDestroyed ? activeEditor : null
    const handler = active ? sujiHandlers.get(active) : undefined
    try {
      handler?.()
    } catch (e) {
      console.warn('[EditorContext] moveToLatestSuji 触发失败:', e)
    }
  }, [activeEditor, sujiHandlers])

  const activeEditorCanSuji = !!activeEditor && !activeEditor.isDestroyed && sujiHandlers.has(activeEditor)

  const setOnImageUpload = useCallback((fn: (() => void) | null, editor?: any) => {
    if (editor) {
      if (fn) {
        uploadHandlersRef.current.set(editor, fn)
      } else {
        uploadHandlersRef.current.delete(editor)
      }
    } else {
      lastUploadHandlerRef.current = fn
    }
  }, [])

  const triggerImageUpload = useCallback(() => {
    // ★ 路由到当前聚焦编辑器的回调：用户光标在哪，图片就插哪。
    // 回退：activeEditor 缺失/已销毁时用最后注册的回调（保底不空转）
    const active = activeEditor && !activeEditor.isDestroyed ? activeEditor : null
    const handler = (active && uploadHandlersRef.current.get(active)) || lastUploadHandlerRef.current
    handler?.()
  }, [activeEditor])

  const openImagePreview = useCallback(
    (src: string, alt?: string, gallery?: { images: string[]; index: number }) => {
      setPreview({ src, alt, images: gallery?.images, index: gallery?.index })
    },
    []
  )

  const closeImagePreview = useCallback(() => {
    setPreview(null)
  }, [])

  return (
    <EditorContext.Provider value={{
      activeEditor,
      setActiveEditor,
      setOnImageUpload,
      triggerImageUpload,
      setOnClipboardAction,
      triggerClipboardCopy,
      triggerClipboardPaste,
      setOnMoveToLatestSuji,
      triggerMoveToLatestSuji,
      activeEditorCanSuji,
      openImagePreview,
      closeImagePreview
    }}>
      {children}
      {/* ★ 全局图片预览 Lightbox：渲染在编辑器容器层，脱离 NodeView 生命周期 */}
      {preview && (
        <ImageLightbox
          src={preview.src}
          alt={preview.alt}
          images={preview.images}
          initialIndex={preview.index}
          onClose={closeImagePreview}
        />
      )}
    </EditorContext.Provider>
  )
}
